import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatDateTime, formatTimestampPrecise } from '@/lib/dates'
import { formatTicketRef } from '@/lib/ticketRef'
import { useEmailLogEntry } from '@/queries/emailLog'
import type { EmailLogDetail, EmailLogEvent } from '@/types/emailLog'
import { EmailPreviewPane } from './EmailPreviewPane'
import { counterparty, emailStatusLabels, emailStatusVariants, kindLabel, openedByLabel, readSummary, reasonLabel, statusBreakdown } from './emailStatus'

const EVENT_LABELS: Record<string, string> = {
  Send: 'Accepted by SES',
  Delivery: 'Delivered',
  Open: 'Opened',
  Click: 'Link clicked',
  Bounce: 'Bounced',
  Complaint: 'Marked as spam by the recipient',
  Reject: 'Rejected by SES',
  DeliveryDelay: 'Delivery delayed',
}

// Machine fetches stay on the timeline so staff can see them, worded so
// nobody mistakes them for a person reading.
function eventLabel(event: EmailLogEvent): string {
  if (event.machine === 'scanner') return event.type === 'Click' ? 'Link checked by a security scanner' : 'Opened by a security scanner'
  if (event.machine === 'prefetch') return event.type === 'Click' ? 'Link prefetched on delivery' : 'Prefetched on delivery'
  return EVENT_LABELS[event.type] ?? event.type
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[96px_1fr] items-baseline gap-x-4 text-etn-control">
      <span className="text-etn-text-soft">{label}</span>
      <span className="min-w-0 break-words text-etn-text-cell">{children}</span>
    </div>
  )
}

function eventDetail(event: EmailLogEvent): string | null {
  const d = event.detail ?? {}
  if (event.type === 'Click' && d.link) return d.link
  const parts = [d.bounceType, d.bounceSubType, d.diagnosticCode, d.complaintFeedbackType, d.reason, d.delayType].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

function Timeline({ events }: { events: EmailLogEvent[] }) {
  if (!events.length) {
    return <p className="text-etn-micro text-etn-text-soft">No delivery events recorded.</p>
  }
  return (
    <ol className="space-y-2 border-l border-etn-border-hairline pl-4" aria-label="Event timeline">
      {events.map(event => {
        const detail = eventDetail(event)
        return (
          <li key={event.eventId} className="text-etn-control">
            <div className="flex items-baseline justify-between gap-3">
              <span className={event.machine ? 'text-etn-text-soft' : 'font-semibold text-etn-text-cell'}>{eventLabel(event)}</span>
              <time dateTime={event.at} className="shrink-0 text-etn-micro tabular-nums text-etn-text-soft">
                {formatTimestampPrecise(event.at)}
              </time>
            </div>
            {detail && <p className="mt-0.5 break-all text-etn-micro text-etn-text-soft">{detail}</p>}
          </li>
        )
      })}
    </ol>
  )
}

type DetailRecipient = EmailLogDetail['recipients'][number]

// Each recipient of a group send is its own SES message, so each has its own
// status, opens and event timeline.
function RecipientBlock({ recipient, open }: { recipient: DetailRecipient; open: boolean }) {
  const reason = reasonLabel(recipient.reason)
  const read = readSummary(recipient)
  return (
    <details open={open} className="rounded-etn-menu border border-etn-border-hairline px-3 py-2" data-testid="email-recipient">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-etn-control [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 flex-1 truncate font-medium text-etn-text-cell">{recipient.address || '—'}</span>
        {read && <span className="shrink-0 text-etn-micro tabular-nums text-etn-text-soft">{read}</span>}
        <Badge variant={emailStatusVariants[recipient.status]}>{emailStatusLabels[recipient.status]}</Badge>
      </summary>
      <div className="mt-2 space-y-2">
        {reason && <p className="text-etn-micro text-etn-text-soft">{reason}</p>}
        <Timeline events={recipient.events ?? []} />
        {recipient.sesMessageId && <p className="font-mono text-etn-micro text-etn-text-soft">SES id {recipient.sesMessageId}</p>}
      </div>
    </details>
  )
}

function Body({ entry }: { entry: EmailLogDetail }) {
  const outbound = entry.direction === 'outbound'
  const reason = reasonLabel(entry.reason)
  const breakdown = statusBreakdown(entry)
  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Row label="Status">
          <Badge variant={emailStatusVariants[entry.status]}>{emailStatusLabels[entry.status]}</Badge>
          {breakdown && <span className="ml-2 text-etn-micro text-etn-text-soft">{breakdown}</span>}
        </Row>
        {openedByLabel(entry) && <Row label="Read">{openedByLabel(entry)}</Row>}
        {reason && <Row label="Reason">{reason}</Row>}
        <Row label={outbound ? 'Sent' : 'Received'}>{formatDateTime(entry.createdAt)}</Row>
        <Row label="From">{entry.from || '—'}</Row>
        <Row label="To">{outbound ? counterparty(entry) : entry.to.join(', ') || '—'}</Row>
        {outbound && <Row label="Kind">{kindLabel(entry)}</Row>}
        {entry.ticket && (
          <Row label="Ticket">
            <Link to={`/admin/tickets/${entry.ticket._id}` as never} className="font-semibold text-etn-accent-text-active hover:underline">
              {formatTicketRef(entry.ticket)}
            </Link>{' '}
            <span className="text-etn-text-soft">{entry.ticket.subject}</span>
          </Row>
        )}
        {outbound && (
          <Row label="Opens">
            {/* Approximate: blocked images under-count, Apple Mail Privacy Protection over-counts. */}
            {entry.openCount}
            {entry.firstOpenedAt ? `, first ${formatDateTime(entry.firstOpenedAt)}` : ''}
            <span className="ml-1 text-etn-micro text-etn-text-soft">(approximate)</span>
          </Row>
        )}
        {outbound && entry.clickCount > 0 && <Row label="Clicks">{entry.clickCount}</Row>}
        {entry.rawS3Key && <Row label="Raw email"><span className="font-mono text-etn-micro">{entry.rawS3Key}</span></Row>}
      </div>
      {outbound && (
        <section>
          <h3 className="mb-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">
            {entry.recipientCount === 1 ? 'Timeline' : `Recipients (${entry.recipientCount})`}
          </h3>
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {entry.recipients.map(r => (
              <RecipientBlock key={r._id} recipient={r} open={entry.recipientCount === 1} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export function EmailDetailDialog({ id, onClose }: { id?: string; onClose: () => void }) {
  const { data, isLoading, error } = useEmailLogEntry(id)
  const [tab, setTab] = useState<'details' | 'preview'>('details')
  return (
    <Dialog open={Boolean(id)} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="pr-6 leading-snug">{data?.subject || (isLoading ? 'Loading…' : '(no subject)')}</DialogTitle>
          <DialogDescription>{data ? (data.direction === 'outbound' ? 'Outbound notification' : 'Inbound email') : ' '}</DialogDescription>
        </DialogHeader>
        {isLoading && <Skeleton className="h-40 w-full" />}
        {error && <p className="text-etn-control text-etn-danger-text">Couldn't load this email.</p>}
        {data && (
          <Tabs value={tab} onValueChange={v => setTab(v as 'details' | 'preview')}>
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="pt-3">
              <Body entry={data} />
            </TabsContent>
            <TabsContent value="preview" className="pt-3">
              <EmailPreviewPane key={data._id} entry={data} enabled={tab === 'preview'} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  )
}
